import { Box, Grid, Typography } from '@mui/material'
import { useContext } from 'react'

import AttachmentSvg from '../../../../assets/icon.attachment.svg'
import RedFlag from '../../../../assets/icon.flag.svg'
import { MessageData } from '../../../../providers/Messages/types'
import { ProviderDetailsContext } from '../../../../providers/ProviderDetails/ProviderDetails.provider'
import { ProviderUser } from '../../../../providers/ProviderDetails/types'
import { themeColors } from '../../../../theme'
import { dateFormatters } from '../../../../utilities/HelperFunctions'
import { useStyles } from './Messaging.styles'

type MessageProps = {
  isStandAloneView?: boolean
  message: MessageData
  keyValue: string
  unread?: boolean
  openThread?: () => void
  attachmentKeys?: { id: number; key: string }[]
  displayUser?: {
    name: string
    role: string
    photoUrl: string | null
    isPatient: boolean
    onCareTeam: boolean
  }
  displayThreadUserNames?: string
  displayUserCount?: number
  myProviderDetails?: ProviderUser | null
}

const MobileMessage = ({
  message,
  keyValue,
  unread,
  openThread,
  attachmentKeys,
}: MessageProps) => {
  const { classes } = useStyles()

  const { myProviderDetails } = useContext(ProviderDetailsContext)

  const messageClick = () => {
    if (openThread) {
      openThread()
    }
  }

  const renderFlags = () => {
    const flags = []
    if (message.urgent) {
      flags.push(
        <div key='urgent' className={classes.message__urgent}>
          <span>High Priority &nbsp;</span>
          <img
            src={RedFlag}
            alt='flag'
            style={{ marginLeft: 10 }}
            key='not-preview'
          />
        </div>
      )
    }

    return <div className={classes.message__flags}>{flags}</div>
  }

  const isImageFile = (filename: string): boolean => {
    const extension =
      filename.split('?')[0].split('.').pop()?.toLowerCase() || ''
    const imageExtensions: string[] = [
      'jpg',
      'jpeg',
      'png',
      'gif',
      'bmp',
      'webp',
      'svg',
    ]
    return imageExtensions.includes(extension)
  }

  const getDisplayFilename = (key: string): string => {
    const fullPath = key.split('?')[0]
    // Get the filename (everything after the last slash)
    const filename = fullPath.substring(fullPath.lastIndexOf('/') + 1)
    // If filename is too long, truncate it
    return filename.length > 20 ? `...${filename.slice(-17)}` : filename
  }

  const renderAttachments = () => {
    return attachmentKeys?.map((k: { id: number; key: string }, i: number) => {
      if (k.id === message.messageId) {
        const isImage = isImageFile(k.key)
        const filename = getDisplayFilename(k.key)

        return isImage ? (
          <a href={k.key} target='_blank' rel='noopener noreferrer'>
            <img
              src={k.key}
              alt={`Attached: ${filename}`}
              style={{
                maxWidth: '100%',
                maxHeight: '200px',
                borderRadius: '8px',
                margin: '6px 0',
              }}
            />
          </a>
        ) : (
          <article
            key={i}
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: isImage ? '100%' : 'max-content',
              margin: '6px 0',
            }}
            className={classes.msgThread__attachment}
          >
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <img
                src={AttachmentSvg}
                alt={'attachment icon'}
                style={{
                  margin: 8,
                }}
              />
              <a href={k.key} target='_blank' rel='noopener noreferrer'>
                {filename}
              </a>
            </div>
          </article>
        )
      } else return null
    })
  }

  const renderHTML = (messageBody: string) => {
    return messageBody
  }

  const checkValidMessageForRender = (message: MessageData) => {
    const isEmptyMessage =
      message.messageBody === '' || message.messageBody === '<p></p>\n'
    const hasAttachments = message.attachments.length > 0
    return !isEmptyMessage || hasAttachments
  }

  if (checkValidMessageForRender(message)) {
    return (
      <Grid item xs={12} sx={{ width: '100%', margin: '0px 0' }}>
        <Grid
          container
          direction='column'
          justifyContent='flex-start'
          key={keyValue}
          onClick={messageClick}
          aria-hidden='true'
          sx={{ width: '100%', wordBreak: 'break-word' }}
        >
          <Grid item xs={2}>
            <Grid container direction='column'>
              <Grid
                item
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                }}
              >
                {unread ? <div className={classes.message__unread} /> : ''}
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={8} sx={{ width: '100%' }}>
            <Grid container direction='column'>
              <Grid
                container
                direction='column'
                alignItems={'flex-start'}
                marginLeft={'none'}
              >
                <Typography
                  className={classes.message__timestamp}
                  sx={{
                    marginBottom: '-6px',
                    alignSelf:
                      message.fromUser.id !== myProviderDetails?.providerId
                        ? 'flex-start'
                        : 'flex-end',
                  }}
                >
                  {message.fromUser.name}
                </Typography>

                {message.messageBody !== '' &&
                  message.messageBody !== '<p></p>\n' && (
                    <Box
                      sx={{
                        width: 'max-content',
                        maxWidth: '70%',
                        height: 'max-content',
                        borderRadius: '10px',
                        bgcolor:
                          message.fromUser.id !== myProviderDetails?.providerId
                            ? themeColors.gray90
                            : themeColors.blue80,
                        alignSelf:
                          message.fromUser.id !== myProviderDetails?.providerId
                            ? 'flex-start'
                            : 'flex-end',
                        margin: '8px 0',
                        padding: '8px 15px',
                      }}
                    >
                      {' '}
                      <Typography
                        sx={{
                          fontSize: '14px',
                          '& p': {
                            margin: 0,
                            padding: 0,
                          },
                        }}
                        dangerouslySetInnerHTML={{
                          __html: renderHTML(message.messageBody),
                        }}
                      ></Typography>
                    </Box>
                  )}

                <Grid
                  item
                  xs={6}
                  sx={{
                    alignSelf:
                      message.fromUser.id !== myProviderDetails?.providerId
                        ? 'flex-start'
                        : 'flex-end',
                  }}
                >
                  {renderAttachments()}
                </Grid>

                <Typography
                  className={classes.message__timestamp}
                  sx={{
                    alignSelf:
                      message.fromUser.id !== myProviderDetails?.providerId
                        ? 'flex-start'
                        : 'flex-end',
                    marginTop: '-5px',
                  }}
                >
                  {' '}
                  {dateFormatters.prettyDateWithTime(message.receivedDateTime)}
                </Typography>

                <Grid item xs={4}>
                  {' '}
                  {renderFlags()}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    )
  } else {
    return <></>
  }
}

export default MobileMessage
